import React, { useEffect, useState } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from 'react-router-dom';
import NavBar from './components/NavBar';
import CardSection from './components/CardSection';
import AdminPage from './components/AdminPage';

function App() {
  const [config, setConfig] = useState(null);

  useEffect(() => {
    // 백엔드 API에서 설정 가져오기
    const fetchConfig = async () => {
      try {
        const apiUrl = process.env.REACT_APP_API_URL || '';
        const response = await fetch(`${apiUrl}/api/config`);
        if (response.ok) {
          const data = await response.json();
          setConfig(data);
          // localStorage에도 캐시
          localStorage.setItem('siteConfig', JSON.stringify(data));
        } else {
          throw new Error('API 요청 실패');
        }
      } catch (error) {
        console.error('설정 로드 실패:', error);
        // API 실패 시 localStorage에서 가져오기
        const savedConfig = localStorage.getItem('siteConfig');
        if (savedConfig) {
          setConfig(JSON.parse(savedConfig));
        } else {
          // 기본 설정
          const defaultConfig = {
        siteTitle: '📌 카드 페이지 템플릿',
        adsenseCode: '',
        tabs: [
          {
            id: 'guide',
            label: '가이드',
            path: '/guide',
            title: '가이드 페이지',
            description: ['이 템플릿을 원하는 주제로 커스터마이징하세요.', 'App.js 파일에서 제목, 설명, 카드 내용을 모두 수정할 수 있습니다.'],
            visible: true,
            blocks: [
              {
                subtitle: '시작하기 전에 알아야 할 것들',
                cards: [
                  { emoji: '📚', title: '첫 번째 카드', description: '여기에 설명을 입력하세요', link: 'https://example.com', bgColor: '#74b9ff' },
                  { emoji: '🎯', title: '두 번째 카드', description: '카드 내용을 자유롭게 수정하세요', link: 'https://example.com', bgColor: '#e17055' },
                  { emoji: '💡', title: '세 번째 카드', description: '링크와 색상을 변경할 수 있습니다', link: 'https://example.com', bgColor: '#fd79a8' },
                  { emoji: '✨', title: '네 번째 카드', description: '이모지도 원하는 대로 바꾸세요', link: 'https://example.com', bgColor: '#a29bfe' },
                ],
              },
              {
                subtitle: '더 많은 정보',
                cards: [
                  { emoji: '🚀', title: '다섯 번째 카드', description: '카드를 추가하거나 제거할 수 있습니다', link: 'https://example.com', bgColor: '#00cec9' },
                  { emoji: '⭐', title: '여섯 번째 카드', description: '반응형 디자인으로 모바일에서도 완벽', link: 'https://example.com', bgColor: '#fdcb6e' },
                ],
              },
            ],
          },
          {
            id: 'recommend',
            label: '추천',
            path: '/recommend',
            title: '추천 페이지',
            description: ['새로운 페이지를 만들 수 있습니다.', 'Routes에 Route를 추가하고 NavBar에 메뉴를 추가하세요.'],
            visible: true,
            blocks: [
              {
                subtitle: '추천 항목',
                cards: [
                  { emoji: '🌟', title: '추천 1', description: '이 페이지의 내용도 자유롭게 수정하세요', link: 'https://example.com', bgColor: '#6c5ce7' },
                  { emoji: '💎', title: '추천 2', description: '여러 페이지를 추가할 수 있습니다', link: 'https://example.com', bgColor: '#00b894' },
                ],
              },
            ],
          },
          {
            id: 'info',
            label: '정보',
            path: '/info',
            title: '정보 페이지',
            description: ['무한히 페이지를 추가할 수 있습니다.', '각 페이지마다 다른 내용을 보여주세요.'],
            visible: true,
            blocks: [
              {
                subtitle: '참고 정보',
                cards: [
                  { emoji: '📖', title: '정보 1', description: 'NavBar에서 메뉴를 추가/수정할 수 있습니다', link: 'https://example.com', bgColor: '#d63031' },
                  { emoji: '🔍', title: '정보 2', description: 'App.js에서 모든 내용을 수정하세요', link: 'https://example.com', bgColor: '#0984e3' },
                ],
              },
            ],
          },
        ],
          };
          setConfig(defaultConfig);
          localStorage.setItem('siteConfig', JSON.stringify(defaultConfig));
        }
      }
    };
    
    fetchConfig();
  }, []);

  // 애드센스 스크립트 로드
  useEffect(() => {
    if (config?.adsenseCode) {
      const existingScript = document.querySelector(
        'script[src*="pagead2.googlesyndication.com"]'
      );

      if (!existingScript) {
        const script = document.createElement('script');
        script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${config.adsenseCode}`;
        script.async = true;
        script.crossOrigin = 'anonymous';
        document.head.appendChild(script);
      }
    }
  }, [config]);

  if (!config) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center">로딩 중...</div>;
  }

  const visibleTabs = config.tabs.filter(tab => tab.visible);

  return (
    <Router>
      <div className="min-h-screen bg-gray-50">
        <NavBar config={config} />
        <Routes>
          {/* 관리자 페이지 */}
          <Route path="/admin" element={<AdminPage />} />
          
          {/* 첫 번째 탭으로 리다이렉트 */}
          <Route 
            path="/" 
            element={<Navigate to={visibleTabs.length > 0 ? visibleTabs[0].path : '/admin'} replace />} 
          />
          
          {/* 동적으로 탭 라우트 생성 */}
          {config.tabs.map((tab) => (
            tab.visible && (
              <Route
                key={tab.id}
                path={tab.path}
                element={
                  <CardSection
                    title={tab.title}
                    description={tab.description}
                    blocks={tab.blocks}
                  />
                }
              />
            )
          ))}
        </Routes>
      </div>
    </Router>
  );
}

export default App;
